// PWA install + service-worker glue. Lets phone players install WHACKHALLA to the home screen and launch it
// fullscreen (manifest display:fullscreen/standalone). The menu shows a dedicated install button on touch
// devices that aren't already installed; this module owns the platform-specific install flow:
//   - Chromium/Android: capture `beforeinstallprompt`, then fire the native prompt on demand.
//   - iOS Safari (no beforeinstallprompt): report 'ios' so the UI can show "Add to Home Screen" steps.
//   - Anything else without a deferred prompt: report 'manual' (use the browser's own install/menu).
// Pure browser concern — no sim coupling, safe to no-op when headless.

let deferredPrompt: BeforeInstallPromptEvent | null = null
let installed = false
const LS_INSTALLED = 'whk_installed'
function markInstalled(): void { installed = true; try { localStorage.setItem(LS_INSTALLED, '1') } catch { /* private mode */ } }
function persistedInstalled(): boolean { try { return localStorage.getItem(LS_INSTALLED) === '1' } catch { return false } }

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Register the service worker and start listening for the install prompt. Call once at boot. */
export function initPWA(): void {
  if (typeof window === 'undefined') return
  // production only — a caching SW in dev would fight Vite HMR
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* offline/install is best-effort */ })
    })
  }
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => { deferredPrompt = null; markInstalled() })
}

/** True when already running as an installed/home-screen app (fullscreen/standalone). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const mm = typeof matchMedia !== 'undefined' &&
    (matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: fullscreen)').matches)
  return Boolean(mm) || (navigator as unknown as { standalone?: boolean }).standalone === true || installed || persistedInstalled()
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return /iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export type InstallResult = 'accepted' | 'dismissed' | 'ios' | 'manual'

/** Trigger the install. Native prompt when available; otherwise signals the UI to show manual steps. */
export async function triggerInstall(): Promise<InstallResult> {
  if (deferredPrompt) {
    try {
      await deferredPrompt.prompt()
      const choice = await deferredPrompt.userChoice
      if (choice.outcome === 'accepted') { deferredPrompt = null; markInstalled() }
      return choice.outcome
    } catch {
      return 'manual'
    }
  }
  return isIOS() ? 'ios' : 'manual'
}

/** Best-effort fullscreen toggle for in-browser play (the installed app is already fullscreen). */
export async function requestFullscreen(el: Element = document.documentElement): Promise<void> {
  try {
    const anyEl = el as Element & { webkitRequestFullscreen?: () => Promise<void> }
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) await el.requestFullscreen()
      else if (anyEl.webkitRequestFullscreen) await anyEl.webkitRequestFullscreen()
    }
  } catch { /* not allowed / unsupported */ }
}
